import "./index.css";

import { Toaster } from "@/components/ui/sonner";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { scan } from "react-scan";
import { PersistGate } from "redux-persist/integration/react";
import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";

import AppInitializer from "./AppInitializer";
import { AppUpdateGate } from "./features/appUpdate";
import { DensityProvider } from "./features/density";
import { ThemeProvider } from "./features/theme";
import { NavbarProvider } from "./hooks/useNavbarContext";
import { QueryProvider } from "./lib/query";
import { persistor, store } from "./redux/store";
import reportWebVitals from "./reportWebVitals";

// Disable console.log in non-development environments
if (process.env.NODE_ENV !== "development") {
  // eslint-disable-next-line no-console
  console.log = () => {};
}

if (typeof window !== "undefined") {
  scan({ enabled: false });
}

if (
  import.meta.env.REACT_APP_VERCEL_ENV === "Production" ||
  import.meta.env.REACT_APP_VERCEL_ENV === "qa"
) {
  Sentry.init({
    dsn: import.meta.env.REACT_APP_SENTRY_DSN,
    environment: import.meta.env.REACT_APP_VERCEL_ENV,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    beforeBreadcrumb() {
      // Disable all breadcrumbs (console logs, HTTP breadcrumbs, etc.)
      return null;
    }
  });
}

if (import.meta.env.REACT_APP_VERCEL_ENV === "Production") {
  mixpanel.init(import.meta.env.REACT_APP_MIXPANEL_TOKEN as string, {
    autocapture: import.meta.env.REACT_APP_MIXPANEL_AUTOCAPTURE === "true",
    record_sessions_percent: 0
  });
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryProvider>
          <ThemeProvider>
            <DensityProvider>
              <NavbarProvider>
                <AppUpdateGate />
                <AppInitializer />
              </NavbarProvider>
              <Toaster position="bottom-center" richColors />
            </DensityProvider>
          </ThemeProvider>
        </QueryProvider>
      </PersistGate>
    </Provider>
  </StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
